import { Link, useParams } from '@tanstack/react-router'
import { Helmet } from '@saas-pages/shared/public/seo'
import { BlogLanguageSwitcher } from '@saas-pages/shared/public/blog'
import { Seo, buildBlogPageMetadata } from '@saas-pages/shared/public/seo'
import { format, useLocale, useT, type Locale } from '@/shared/i18n'
import branding from '../../../branding.config'
import { getAllPosts, getPostBySlug } from '@/shared/blog/posts'
import { buildUtilities } from '@/shared/utilities/utilities'
import { ToolFaq } from '@/shared/seo'

function localeName(target: Locale, t: ReturnType<typeof useT>): string {
  return target === 'en' ? t.langSwitcher.en : t.langSwitcher.ru
}

function formatDate(iso: string, locale: 'ru' | 'en'): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function localeHref(post: NonNullable<ReturnType<typeof getPostBySlug>>): Partial<Record<Locale, string>> {
  return {
    [post.locale]: `/blog/${post.slug}`,
    ...Object.fromEntries(
      Object.entries(post.translations)
        .filter((entry): entry is [Locale, string] => Boolean(entry[1]))
        .map(([locale, slug]) => [locale, `/blog/${slug}`]),
    ),
  }
}

export function BlogPostPage() {
  const t = useT()
  const { locale, setLocale } = useLocale()
  const { slug } = useParams({ from: '/blog/$slug' })
  const post = getPostBySlug(slug)

  if (!post) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex" />
          <title>{t.blog.notFoundDoc}</title>
        </Helmet>
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold tracking-tight">{t.blog.notFoundTitle}</h1>
          <p className="mt-4 text-muted-foreground">
            {t.blog.notFoundBody}
          </p>
          <Link to="/blog" className="mt-6 inline-block text-primary hover:underline">
            {t.blog.backToBlog}
          </Link>
        </div>
      </>
    )
  }

  const metadata = buildBlogPageMetadata(post, getAllPosts(), {
    domain: branding.domain,
    brandName: branding.domain,
    defaultOgImage: branding.ogImage,
    titleSeparator: ' — ',
  })
  const translationSlug = post.translations[locale]
  const languageHrefs = localeHref(post)
  const PostContent = post.Component
  const utilities = buildUtilities(t)
  const relatedTools = post.relatedTools
    .map((key) => utilities.find((u) => u.key === key))
    .filter((u): u is NonNullable<typeof u> => u !== undefined && u.status === 'available')


  return (
    <>
      <Seo metadata={metadata} />

      <article className="mx-auto max-w-3xl">
        <nav aria-label="breadcrumb" className="mb-6 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center">
            <li>
              <Link to="/" className="hover:text-foreground">
                pdfy.ru
              </Link>
            </li>
            <li aria-hidden="true" className="mx-2">/</li>
            <li>
              <Link to="/blog" className="hover:text-foreground">
                {t.blog.breadcrumb}
              </Link>
            </li>
            <li aria-hidden="true" className="mx-2">/</li>
            <li aria-current="page" className="truncate text-foreground">
              {post.title}
            </li>
          </ol>
        </nav>

        {post.locale !== locale && (
          <aside
            role="note"
            className="mb-6 rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm text-foreground/90"
          >
            <p>{format(t.blog.otherLanguage, { language: localeName(post.locale, t) })}</p>
            {translationSlug && (
              <p className="mt-2">
                <Link
                  to="/blog/$slug"
                  params={{ slug: translationSlug }}
                  className="text-primary hover:underline"
                >
                  {format(t.blog.viewTranslation, { language: localeName(locale, t) })}
                </Link>
              </p>
            )}
          </aside>
        )}

        <header className="mb-8">
          <div className="mb-4 flex justify-end">
            <BlogLanguageSwitcher
              currentLocale={post.locale}
              ariaLabel={t.langSwitcher.aria}
              hrefs={languageHrefs}
              disableMissingHrefs
              onSelectLocale={setLocale}
              labels={{
                ru: t.langSwitcher.ru,
                en: t.langSwitcher.en,
              }}
              renderLink={(_, href, className, children, onClick) => (
                <Link
                  to={href}
                  className={className}
                  onClick={onClick}
                >
                  {children}
                </Link>
              )}
            />
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {post.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <time dateTime={post.date}>{formatDate(post.date, locale)}</time>
            {post.updated && post.updated !== post.date && (
              <>
                <span aria-hidden>·</span>
                <span>
                  {t.blog.updated}: <time dateTime={post.updated}>{formatDate(post.updated, locale)}</time>
                </span>
              </>
            )}
            {post.tags.length > 0 && (
              <>
                <span aria-hidden>·</span>
                <span>{post.tags.join(', ')}</span>
              </>
            )}
          </div>
        </header>

        <div className="prose prose-slate max-w-none prose-headings:tracking-tight prose-headings:text-foreground prose-p:text-foreground/90 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-code:text-foreground prose-code:rounded prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:font-normal prose-code:before:content-none prose-code:after:content-none">
          <PostContent />
        </div>

        <ToolFaq items={post.faq} heading={t.blog.faqHeading} />

        {relatedTools.length > 0 && (
          <section
            aria-label={t.blog.relatedToolsLabel}
            className="mt-12 rounded-xl border bg-muted/30 p-6"
          >
            <h2 className="mb-4 text-base font-semibold text-foreground">
              {t.blog.relatedToolsHeading}
            </h2>
            <ul className="space-y-3">
              {relatedTools.map((tool) => (
                <li key={tool.key}>
                  <Link
                    to={tool.path}
                    className="flex items-start gap-3 text-sm text-foreground hover:text-primary"
                  >
                    <span aria-hidden className="text-xl">{tool.icon}</span>
                    <span>
                      <span className="font-medium">{tool.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {tool.shortDescription}
                      </span>
                    </span>
                    <span className="ml-auto self-center text-xs text-muted-foreground">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-10 text-sm">
          <Link to="/blog" className="text-primary hover:underline">
            {t.blog.backToBlog}
          </Link>
        </div>
      </article>
    </>
  )
}
